import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Shield } from 'lucide-react';

const LoginPage = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showTestModeOption, setShowTestModeOption] = useState(false);

    const { login, user, isTestMode, toggleTestMode } = useAuth();
    const navigate = useNavigate();

    // 如果已登入，重定向到首頁
    if (user) {
        return <Navigate to="/dashboard" replace />;
    }


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!username || !password) {
            setError('請輸入帳號和密碼');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            await login(username, password);
            navigate('/dashboard');
        } catch (error) {
            setError(error.response?.data?.message || error.message || '登入失敗，請檢查您的帳號和密碼');
        } finally {
            setIsLoading(false);
        }
    };

    const handleTestModeToggle = (e) => {
        toggleTestMode(e.target.checked);
    };

    // 切換測試模式選項可見性
    const handleKeyDown = (e) => {
        // 按下 Ctrl+Alt+T 時顯示測試模式
        if (e.ctrlKey && e.altKey && e.key === 't') {
            setShowTestModeOption(!showTestModeOption);
            e.preventDefault();
        }
    };

    return (
        <div className="h-full bg-white p-6 flex flex-col" onKeyDown={handleKeyDown} tabIndex="0">
            <div className="flex-1 flex flex-col items-center justify-center">
                <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mb-6">
                    <Shield className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold mb-6">SmartRide 管理後台</h2>

                {error && (
                    <div className="w-full max-w-md mb-4 p-3 bg-red-50 text-red-600 rounded-md">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="w-full max-w-md">
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">管理員帳號</label>
                        <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="輸入您的管理員帳號"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            disabled={isLoading}
                        />
                    </div>
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">密碼</label>
                        <input
                            type="password"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="輸入您的密碼"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={isLoading}
                        />
                    </div>

                    {showTestModeOption && (
                        <div className="mb-4 flex items-center">
                            <input
                                type="checkbox"
                                id="testMode"
                                checked={isTestMode}
                                onChange={handleTestModeToggle}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="testMode" className="ml-2 block text-sm text-gray-700">
                                啟用測試模式 (無後端)
                            </label>
                        </div>
                    )}

                    <button
                        type="submit"
                        className="w-full bg-gray-800 text-white py-3 rounded-md font-medium hover:bg-gray-700 disabled:bg-gray-400"
                        disabled={isLoading}
                    >
                        {isLoading ? '登入中...' : '登入'}
                    </button>
                </form>

                {isTestMode && (
                    <div className="mt-4 w-full max-w-md p-4 bg-blue-50 rounded-md">
                        <p className="text-sm font-medium text-blue-800 mb-2">測試模式已啟用</p>
                        <p className="text-xs text-blue-700 mb-1">您可以使用以下測試帳號:</p>
                        <ul className="text-xs text-blue-700 list-disc pl-5">
                            <li>管理員: admin / admin123</li>
                            <li>車隊經理: manager / manager123</li>
                            <li>調度員: dispatcher / dispatch123</li>
                        </ul>
                    </div>
                )}

                <p className="mt-4 text-sm text-gray-600">
                    系統問題？<a href="mailto:support@smartride.com" className="text-blue-500">聯絡技術支援</a>
                </p>

                <div className="mt-2 text-xs text-gray-400">
                    按下 Ctrl+Alt+T 可切換測試模式選項
                </div>
            </div>
        </div>
    );
};

export default LoginPage;